import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronLeftIcon, PhoneIcon, MailIcon, MapPinIcon, CalendarIcon, BuildingIcon, FileTextIcon, ClockIcon, ArrowLeftIcon, DownloadIcon, SendIcon, PrinterIcon, CreditCardIcon, HashIcon } from 'lucide-react';
export const RequestDetailsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const request = location.state?.request || {
    id: 'BLA-2023-10475',
    submittedDate: 'October 15, 2023',
    applicantName: 'Sarah Johnson',
    phone: '(555) 123-4567',
    email: 'sarah.johnson@example.com',
    address: '123 Main Street, Suite 200, Anytown, CA 94103',
    businessName: 'Evergreen Consulting Services',
    businessType: 'Limited Liability Company (LLC)',
    status: 'in-review'
  };
  const documents = [{
    name: 'Business Formation Documents',
    status: 'approved',
    date: 'Oct 18, 2023'
  }, {
    name: 'Proof of Identity',
    status: 'approved',
    date: 'Oct 18, 2023'
  }, {
    name: 'Business Plan',
    status: 'in-review',
    date: 'Oct 19, 2023'
  }, {
    name: 'Certificate of Occupancy',
    status: 'pending',
    date: 'Awaiting Review'
  }, {
    name: 'Tax Registration',
    status: 'pending',
    date: 'Awaiting Review'
  }];
  // Update the timeline data structure near the top of the component
  const timeline = [{
    status: 'Submitted',
    date: 'Oct 15, 2023',
    description: 'Application submitted online',
    completed: true,
    color: 'text-emerald-600'
  }, {
    status: 'Received',
    date: 'Oct 16, 2023',
    description: 'Application received by Business Licensing Department',
    completed: true,
    color: 'text-emerald-600'
  }, {
    status: 'Document Review',
    date: 'Oct 18, 2023',
    description: 'Initial document review in progress',
    completed: true,
    color: 'text-emerald-600'
  }, {
    status: 'In Review',
    date: 'Oct 19, 2023',
    description: 'Application under review by Business License Division',
    completed: false,
    color: 'text-blue-600'
  }, {
    status: 'Pending Approval',
    date: '',
    description: '',
    completed: false,
    color: 'text-gray-400'
  }, {
    status: 'Approved',
    date: '',
    description: '',
    completed: false,
    color: 'text-gray-400'
  }];
  const comments = [{
    author: 'John Smith',
    date: 'Oct 18, 2023',
    content: 'Requested additional information regarding business structure.'
  }, {
    author: 'Sarah Johnson',
    date: 'Oct 19, 2023',
    content: 'Uploaded revised business formation documents as requested.'
  }];
  // Add payment information
  const paymentInfo = {
    licenseType: 'Standard Business License',
    licenseFee: '$450.00',
    status: 'Paid',
    paymentDate: 'October 15, 2023',
    paymentMethod: 'Visa ending in 4242',
    transactionId: 'TXN-2023-1234567',
    nextRenewalDate: 'October 15, 2024'
  };
  const [selectedAction, setSelectedAction] = useState('');
  const reasonCodes = {
    'Approve Application': ['Meets All Requirements', 'Conditional Approval', 'Expedited Process'],
    'Request Changes': ['Missing Documentation', 'Incomplete Information', 'Additional Verification Needed'],
    'Reject Application': ['Non-Compliant', 'Failed Background Check', 'Invalid Business Type']
  };
  return <div className="min-h-screen bg-gray-50" data-id="element-4699">
      <div className="max-w-[1400px] mx-auto px-4 py-6" data-id="element-4700">
        {/* Header */}
        <div className="mb-6" data-id="element-4701">
          <button onClick={() => navigate(-1)} className="flex items-center text-gray-600 hover:text-gray-900 mb-4" data-id="element-4702">
            <ArrowLeftIcon className="h-4 w-4 mr-1" data-id="element-4703" />
            Back to Applications
          </button>
          <div className="flex justify-between items-center" data-id="element-4704">
            <div data-id="element-4705">
              <h1 className="text-2xl font-semibold text-gray-900 mb-3" data-id="element-4706">
                Business License Application
              </h1>
              <div className="flex gap-2" data-id="element-4707">
                <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-md text-sm" data-id="element-4708">
                  Application ID: {request.id}
                </span>
                <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-md text-sm" data-id="element-4709">
                  Submitted: October 15, 2023 • 12:30:45
                </span>
              </div>
            </div>
            <div className="flex items-center gap-3" data-id="element-4710">
              <div className="bg-blue-50 text-blue-700 px-4 py-1.5 rounded-full flex items-center gap-2" data-id="element-4711">
                <ClockIcon className="h-4 w-4" data-id="element-4712" />
                <span className="text-sm font-medium" data-id="element-4713">In Review</span>
              </div>
              <button className="p-2 text-gray-600 hover:text-gray-700 border border-gray-200 rounded-md" title="Print Application" data-id="element-4714">
                <PrinterIcon className="h-5 w-5" data-id="element-4715" />
              </button>
              <button className="p-2 text-gray-600 hover:text-gray-700 border border-gray-200 rounded-md" title="Download Application" data-id="element-4716">
                <DownloadIcon className="h-5 w-5" data-id="element-4717" />
              </button>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-4718">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6" data-id="element-4719">
            {/* Applicant Information */}
            <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-4720">
              <h2 className="text-lg font-medium mb-4" data-id="element-4721">
                Applicant Information
              </h2>
              <div className="grid grid-cols-2 gap-4" data-id="element-4722">
                <div data-id="element-4723">
                  <div className="text-sm text-gray-500" data-id="element-4724">Full Name</div>
                  <div className="font-medium" data-id="element-4725">{request.applicantName}</div>
                </div>
                <div data-id="element-4726">
                  <div className="text-sm text-gray-500" data-id="element-4727">Phone Number</div>
                  <div className="font-medium" data-id="element-4728">{request.phone}</div>
                </div>
                <div data-id="element-4729">
                  <div className="text-sm text-gray-500" data-id="element-4730">Email Address</div>
                  <div className="font-medium" data-id="element-4731">{request.email}</div>
                </div>
                <div data-id="element-4732">
                  <div className="text-sm text-gray-500" data-id="element-4733">Address</div>
                  <div className="font-medium" data-id="element-4734">{request.address}</div>
                </div>
              </div>
            </div>
            {/* Business Information */}
            <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-4735">
              <h2 className="text-lg font-medium mb-4" data-id="element-4736">Business Information</h2>
              <div className="grid grid-cols-2 gap-4" data-id="element-4737">
                <div data-id="element-4738">
                  <div className="text-sm text-gray-500" data-id="element-4739">Business Name</div>
                  <div className="font-medium" data-id="element-4740">{request.businessName}</div>
                </div>
                <div data-id="element-4741">
                  <div className="text-sm text-gray-500" data-id="element-4742">Business Type</div>
                  <div className="font-medium" data-id="element-4743">{request.businessType}</div>
                </div>
                <div data-id="element-4744">
                  <div className="text-sm text-gray-500" data-id="element-4745">Start Date</div>
                  <div className="font-medium" data-id="element-4746">January 1, 2024</div>
                </div>
                <div data-id="element-4747">
                  <div className="text-sm text-gray-500" data-id="element-4748">
                    Number of Employees
                  </div>
                  <div className="font-medium" data-id="element-4749">5-10</div>
                </div>
              </div>
            </div>
            {/* Supporting Documents */}
            <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-4750">
              <h2 className="text-lg font-medium mb-4" data-id="element-4751">Supporting Documents</h2>
              <div className="space-y-3" data-id="element-4752">
                {documents.map((doc, index) => <div key={index} className="flex items-center justify-between py-2" data-id="element-4753">
                    <div className="flex items-center" data-id="element-4754">
                      <FileTextIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-4755" />
                      <span className="text-sm font-medium" data-id="element-4756">{doc.name}</span>
                    </div>
                    <div className="flex items-center" data-id="element-4757">
                      <span className={`text-sm ${doc.status === 'approved' ? 'text-green-600' : doc.status === 'in-review' ? 'text-blue-600' : 'text-gray-500'}`} data-id="element-4758">
                        {doc.status === 'approved' ? 'Approved' : doc.status === 'in-review' ? 'In Review' : 'Pending'}
                      </span>
                    </div>
                  </div>)}
              </div>
            </div>
            {/* Payment Information */}
            <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-4759">
              <h2 className="text-lg font-medium mb-6" data-id="element-4760">Payment Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4761">
                <div className="flex items-start gap-3" data-id="element-4762">
                  <FileTextIcon className="w-5 h-5 text-gray-400 mt-0.5" data-id="element-4763" />
                  <div data-id="element-4764">
                    <div className="text-sm text-gray-500" data-id="element-4765">License Type</div>
                    <div className="font-medium text-gray-900" data-id="element-4766">
                      {paymentInfo.licenseType}
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-4767">
                  <FileTextIcon className="w-5 h-5 text-gray-400 mt-0.5" data-id="element-4768" />
                  <div data-id="element-4769">
                    <div className="text-sm text-gray-500" data-id="element-4770">License Fee</div>
                    <div className="font-medium text-gray-900" data-id="element-4771">
                      {paymentInfo.licenseFee}
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-4772">
                  <div className="w-5 h-5 text-green-500 mt-0.5" data-id="element-4773" />
                  <div data-id="element-4774">
                    <div className="text-sm text-gray-500" data-id="element-4775">Payment Status</div>
                    <div className="font-medium text-green-600" data-id="element-4776">
                      {paymentInfo.status}
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-4777">
                  <CalendarIcon className="w-5 h-5 text-gray-400 mt-0.5" data-id="element-4778" />
                  <div data-id="element-4779">
                    <div className="text-sm text-gray-500" data-id="element-4780">Payment Date</div>
                    <div className="font-medium text-gray-900" data-id="element-4781">
                      {paymentInfo.paymentDate}
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-4782">
                  <CreditCardIcon className="w-5 h-5 text-gray-400 mt-0.5" data-id="element-4783" />
                  <div data-id="element-4784">
                    <div className="text-sm text-gray-500" data-id="element-4785">Payment Method</div>
                    <div className="font-medium text-gray-900" data-id="element-4786">
                      {paymentInfo.paymentMethod}
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-4787">
                  <HashIcon className="w-5 h-5 text-gray-400 mt-0.5" data-id="element-4788" />
                  <div data-id="element-4789">
                    <div className="text-sm text-gray-500" data-id="element-4790">Transaction ID</div>
                    <div className="font-medium text-gray-900" data-id="element-4791">
                      {paymentInfo.transactionId}
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-4792">
                  <CalendarIcon className="w-5 h-5 text-gray-400 mt-0.5" data-id="element-4793" />
                  <div data-id="element-4794">
                    <div className="text-sm text-gray-500" data-id="element-4795">
                      Next Renewal Date
                    </div>
                    <div className="font-medium text-gray-900" data-id="element-4796">
                      {paymentInfo.nextRenewalDate}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Notes & Comments */}
            <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-4797">
              <h2 className="text-lg font-medium mb-4" data-id="element-4798">Notes & Comments</h2>
              <div className="space-y-4" data-id="element-4799">
                {comments.map((comment, index) => <div key={index} className="border-b border-gray-100 pb-4 last:border-0" data-id="element-4800">
                    <div className="flex justify-between items-center mb-2" data-id="element-4801">
                      <span className="font-medium" data-id="element-4802">{comment.author}</span>
                      <span className="text-sm text-gray-500" data-id="element-4803">
                        {comment.date} • 12:30:45
                      </span>
                    </div>
                    <p className="text-sm text-gray-600" data-id="element-4804">{comment.content}</p>
                  </div>)}
                <div className="mt-4" data-id="element-4805">
                  <textarea className="w-full border border-gray-300 rounded-md p-2 text-sm" rows={3} placeholder="Type your comment here..." data-id="element-4806" />
                  <button className="mt-2 flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700" data-id="element-4807">
                    <SendIcon className="h-4 w-4 mr-2" data-id="element-4808" />
                    Add Comment
                  </button>
                </div>
              </div>
            </div>
          </div>
          {/* Right Sidebar - Application Status */}
          <div className="lg:col-span-1" data-id="element-4809">
            <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-4810">
              <h2 className="text-lg font-medium mb-6" data-id="element-4811">Application Status</h2>
              <div className="relative space-y-8" data-id="element-4812">
                {/* Vertical Timeline Line */}
                <div className="absolute left-[15px] top-[24px] bottom-2 w-[2px] bg-gray-100" data-id="element-4813" />
                {timeline.map((event, index) => <div key={index} className="relative flex items-start" data-id="element-4814">
                    <div className="relative flex items-center justify-center" data-id="element-4815">
                      {event.completed ? <div className="rounded-full bg-emerald-100 p-1" data-id="element-4816">
                          <div className="w-5 h-5 text-emerald-600" data-id="element-4817" />
                        </div> : event.color === 'text-blue-600' ? <div className="rounded-full bg-blue-100 p-1.5" data-id="element-4818">
                          <div className="w-4 h-4 rounded-full bg-blue-600" data-id="element-4819" />
                        </div> : <div className="rounded-full bg-gray-100 p-1.5" data-id="element-4820">
                          <div className="w-4 h-4 rounded-full bg-gray-300" data-id="element-4821" />
                        </div>}
                    </div>
                    <div className="ml-4 min-w-0 flex-1" data-id="element-4822">
                      <div className={`text-base font-medium ${event.color}`} data-id="element-4823">
                        {event.status}
                      </div>
                      {event.date && <div className="text-sm text-gray-500 mt-0.5" data-id="element-4824">
                          {event.date} • 12:30:45
                        </div>}
                      {event.description && <div className="mt-1 text-sm text-gray-600" data-id="element-4825">
                          {event.description}
                        </div>}
                    </div>
                  </div>)}
              </div>
              {/* Action Required Section */}
              <div className="mt-6 pt-6 border-t border-gray-200" data-id="element-4826">
                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4" data-id="element-4827">
                  <div className="flex items-center gap-2 mb-2" data-id="element-4828">
                    <div className="w-5 h-5 text-yellow-700" data-id="element-4829" />
                    <h3 className="text-sm font-medium text-gray-900" data-id="element-4830">
                      Action Required
                    </h3>
                  </div>
                  <p className="text-sm text-gray-600 mb-4" data-id="element-4831">
                    This application requires review of the business plan
                    document before proceeding.
                  </p>
                  <div className="space-y-4" data-id="element-4832">
                    <div data-id="element-4833">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4834">
                        Select Action
                      </label>
                      <select className="w-full border border-gray-300 rounded-lg p-2.5 text-sm bg-white" value={selectedAction} onChange={e => setSelectedAction(e.target.value)} data-id="element-4835">
                        <option value="" data-id="element-4836">Select an action...</option>
                        <option value="Approve Application" data-id="element-4837">
                          Approve Application
                        </option>
                        <option value="Request Changes" data-id="element-4838">Request Changes</option>
                        <option value="Reject Application" data-id="element-4839">
                          Reject Application
                        </option>
                      </select>
                    </div>
                    {selectedAction && <div data-id="element-4840">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4841">
                          Select Reason Code
                        </label>
                        <select className="w-full border border-gray-300 rounded-lg p-2.5 text-sm bg-white" data-id="element-4842">
                          <option value="" data-id="element-4843">Select a reason...</option>
                          {reasonCodes[selectedAction]?.map(reason => <option key={reason} value={reason} data-id="element-4844">
                              {reason}
                            </option>)}
                        </select>
                      </div>}
                    <button className="w-full bg-blue-600 text-white py-2.5 px-4 rounded-lg hover:bg-blue-700 transition-colors" data-id="element-4845">
                      Submit Action
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
};